import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Power, PlugZap, LogOut } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";
import { useKiteStatus } from "@/components/Shell";

function Funds() {
  const [f, setF] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try {
      const d = (await api.get("/kite/funds")).data;
      if (d.error) setErr(d.error); else { setF(d); setErr(null); }
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, err ? 15000 : 60000); return () => clearInterval(id); }, [load, err]);
  if (err) return <p className="mt-2 text-[11px] text-amber-300" data-testid="funds-error">{err}</p>;
  if (!f) return null;
  return (
    <div className="mt-3 pt-3 border-t border-[#1E293B] text-xs" data-testid="funds-panel">
      <div className="flex justify-between"><span className="text-slate-400">Available margin</span>
        <span className="num text-slate-100" data-testid="funds-available">{inr(f.net)}</span></div>
      {f.need_est != null && <div className="flex justify-between mt-1"><span className="text-slate-400">₹{(f.trade_value / 1e5).toFixed(1)} L trade needs ≈</span>
        <span className="num text-slate-300">{inr(f.need_est)}</span></div>}
      {f.ok === false && <p className="mt-2 text-red-400 font-medium" data-testid="funds-short-alert">Margin short — Zerodha will reject a ₹{(f.trade_value / 1e5).toFixed(1)} L order. Add funds or lower the position value.</p>}
    </div>
  );
}

export default function KitePanel() {
  const [s, reload] = useKiteStatus();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("kite");
    if (q === "ok") toast.success("Zerodha session active for today");
    if (q === "failed") toast.error("Zerodha login failed — try again");
    if (q) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const login = async () => {
    setBusy(true);
    try { window.location.href = (await api.get("/kite/login_url")).data.url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const logout = async () => { await api.post("/kite/logout"); reload(); };

  const checkIp = async () => {
    const r = (await api.get("/kite/order_ip_check")).data;
    if (r.ok) toast.success(`Orders leave from ${r.ip} — matches your whitelisted IP`);
    else toast.error(r.error || `Orders leave from ${r.ip}, expected ${r.expected}`);
  };

  if (!s) return <div className="panel p-4 label-xs">Checking Kite…</div>;
  const orderRoute = s.static_ip && (
    <div className="mt-3 pt-3 border-t border-[#1E293B] text-xs" data-testid="order-ip-panel">
      <div className="flex items-center justify-between">
        <span className="text-slate-400">Order IP <span className="num text-slate-200">{s.static_ip}</span></span>
        <span className={s.order_route ? "text-cyan-300" : "text-amber-300"} data-testid="order-route-state">{s.order_route ? "route set" : "route not set"}</span>
      </div>
      {s.order_route
        ? <button onClick={checkIp} data-testid="order-ip-check-btn" className="mt-2 text-cyan-300 hover:underline">Check order IP</button>
        : <p className="mt-1 text-slate-500">Trade mode stays off until orders can go out through this IP.</p>}
    </div>
  );
  return (
    <div className="panel p-4" data-testid="kite-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="label-xs">Zerodha Kite</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${s.logged_in ? "border-cyan-500/40 text-cyan-300" : "border-amber-500/40 text-amber-300"}`} data-testid="kite-session-state">
          {s.logged_in ? "session active" : s.configured ? "logged out" : "not set up"}
        </span>
      </div>
      {!s.configured && (
        <p className="text-xs text-slate-400 leading-relaxed" data-testid="kite-not-configured">
          Add your Kite Connect API key and secret to use live data and real orders. Until then, use <b className="text-slate-200">Sample replay</b> to try every mode.
          Set your Kite app redirect URL to <span className="num text-cyan-300 break-all">{s.redirect_url}</span>
        </p>
      )}
      {s.configured && !s.logged_in && (
        <>
          <p className="text-xs text-slate-400 mb-3">Kite tokens expire daily. Log in once each trading day.</p>
          <button onClick={login} disabled={busy} data-testid="kite-login-btn"
            className="w-full h-11 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold flex items-center justify-center gap-2 transition-colors">
            <Power size={16} /> Login to Zerodha
          </button>
        </>
      )}
      {s.logged_in && (
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-slate-200"><PlugZap size={14} className="text-cyan-300" />{s.user_name || s.user_id}</span>
          <button onClick={logout} data-testid="kite-logout-btn" className="text-xs text-slate-400 hover:text-slate-100 flex items-center gap-1"><LogOut size={12} />Log out</button>
        </div>
      )}
      {s.logged_in && <Funds />}
      {s.configured && orderRoute}
    </div>
  );
}
